import type { IAPIAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/abstract/IAPIAdapter';
import { AbstractDataServiceFeature } from 'plugins/tag/plugin_data_service/ts/Application/abstract/AbstractDataServiceFeature';
import { formatEpisodesRemaining } from 'plugins/tag/plugin_data_service/ts/Application/features/UEC/formatEpisodesRemaining';
import { IDataServiceFeatureContext } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeatureContext';
import type { INextItemEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/INextItemEndpoint';
import type { INextItemFetchParams } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/INextItemFetchParams';
import type { INextItemFetchResult } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/nextItem/INextItemFetchResult';
import type { INextItemPayload } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/nextItem/INextItemPayload';

type IUECRawUpNextBody = {
	apiResponse?: unknown;
};

const UEC_CONTENT_TYPES = {
	SHOW: 'show',
	MOVIE: 'movie',
	LIVE: 'live'
} as const;

function nextItemEndpointAdapter( adapter: IAPIAdapter | null ): INextItemEndpoint | null {
	if ( adapter == null ) {
		return null;
	}

	const candidate = adapter as unknown as INextItemEndpoint;
	if ( typeof candidate.fetchNextItemRecommendations !== 'function' ) {
		return null;
	}

	return candidate;
}

/**
 * @copyright © Paramount Streaming
 * @description Data Service feature for Pluto episode-to-episode (next item) recommendations.
 * Mirrors {@link UECFeature} but calls a separate HTTP URL via the adapter's {@link INextItemEndpoint}.
 * Exposed as `tag.API.DATA.nextItem.fetchUpNextRecommendations`.
 */
export class NextItemFeature extends AbstractDataServiceFeature {
	get name (): string {
		return 'NextItemFeature';
	}

	get featureKey (): string {
		return 'nextItem';
	}

	getPublicAPI (): Record<string, unknown> {
		return {
			fetchNextItemRecommendations: this.#fetchNextItemRecommendations
		};
	}

	protected doInstall( _context: IDataServiceFeatureContext ): void {
		// Adapter is resolved lazily on each fetch via APIService.getActiveAdapter() (same active adapter as UEC).
	}

	#fetchNextItemRecommendations = async ( requestPayload: INextItemPayload ): Promise<unknown> => {
		const adapter = nextItemEndpointAdapter( this.getApiService().getActiveAdapter() );
		if ( !adapter ) {
			throw new Error( '[NextItemFeature] next-item endpoint not available on the active adapter' );
		}

		const params = this.#mapPayloadToParams( requestPayload );
		this.logger?.log( '[NextItemFeature]', 'fetchUpNextRecommendations params:', params );

		const rawResponse = await adapter.fetchNextItemRecommendations( params, this.context?.tag?.params );
		this.logger?.log( '[NextItemFeature]', 'fetchUpNextRecommendations response:', rawResponse as never );

		return this.#postProcess( rawResponse, requestPayload );
	};

	#postProcess = ( rawResponse: unknown, requestPayload: INextItemPayload ): INextItemFetchResult => {
		let apiResponse: unknown = rawResponse;

		if ( rawResponse && typeof rawResponse === 'object' ) {
			const body = rawResponse as IUECRawUpNextBody;
			if ( body.apiResponse !== undefined ) {
				apiResponse = body.apiResponse;
			}
		}

		if ( Array.isArray( apiResponse ) && apiResponse.length === 0 ) {
			this.logger?.warn( '[NextItemFeature] Normalizing empty apiResponse [] → { recommendations: [] }' );
			apiResponse = { recommendations: [] };
		}

		const result: INextItemFetchResult = { apiResponse };
		const contentType = requestPayload?.contentType?.toLowerCase();

		if ( contentType && contentType !== UEC_CONTENT_TYPES.LIVE ) {
			const raw = apiResponse && typeof apiResponse === 'object' ? ( apiResponse as Record<string, unknown> ) : undefined;
			const nested = raw?.apiResponse && typeof raw.apiResponse === 'object'
				? ( raw.apiResponse as Record<string, unknown> )
				: undefined;
			const recommendationsForEpisodesRemaining = raw?.recommendations ?? nested?.recommendations;
			result.episodesRemaining = formatEpisodesRemaining(
				!!requestPayload?.cpEpisodesRemainingEnabled,
				recommendationsForEpisodesRemaining
			);
		}

		return result;
	};

	/**
	 * @description Maps the Skin-layer payload (from CardUtilities.getEndCardAPIPayload)
	 * to INextItemFetchParams for the next-item endpoint.
	 *
	 * Mapping (distinct from {@link UECFeature} — upstream next-item expects the playing asset id):
	 *   show  -> endCardId = contentId (episode), contentType = 'show'
	 *   movie -> endCardId = contentId, contentType = 'movie'
	 *   live  -> endCardId = listingId, contentType = 'live', channelSlug required
	 */
	#mapPayloadToParams = ( payload: INextItemPayload ): INextItemFetchParams => {
		const normalizedType = payload?.contentType?.toLowerCase();

		let endCardId: string;
		let contentType: INextItemFetchParams[ 'contentType' ];

		switch ( normalizedType ) {
			case UEC_CONTENT_TYPES.SHOW:
				endCardId = payload.contentId ?? payload.seriesId ?? '';
				contentType = UEC_CONTENT_TYPES.SHOW;
				break;
			case UEC_CONTENT_TYPES.MOVIE:
				endCardId = payload.contentId ?? '';
				contentType = UEC_CONTENT_TYPES.MOVIE;
				break;
			case UEC_CONTENT_TYPES.LIVE:
				endCardId = String( payload.listingId ?? '' );
				contentType = UEC_CONTENT_TYPES.LIVE;
				break;
			default:
				throw new Error( `[NextItemFeature] unsupported contentType: "${ payload?.contentType }"` );
		}

		if ( !endCardId ) {
			throw new Error( `[NextItemFeature] endCardId could not be resolved for contentType: "${ contentType }"` );
		}

		const params: INextItemFetchParams = {
			endCardId,
			contentType
		};

		if ( payload._clientRegion ) {
			params._clientRegion = payload._clientRegion;
		}
		if ( payload.channelSlug ) {
			params.channelSlug = payload.channelSlug;
		}
		if ( payload.device ) {
			params.device = payload.device;
		}
		if ( payload.start !== undefined ) {
			params.start = payload.start;
		}
		if ( payload.rows !== undefined ) {
			params.rows = payload.rows;
		}

		if ( payload.contentId ) {
			params.contentId = payload.contentId;
			if ( contentType === UEC_CONTENT_TYPES.SHOW ) {
				params.episodeId = payload.contentId;
				if ( payload.seriesId ) {
					params.showId = payload.seriesId;
				}
			}
		}

		return params;
	};
}